import type {TutorialLessonData} from './types';

function p(rows: string[]): boolean[][] {
    return rows.map((r) => [...r].map((c) => c === '1'));
}

export const TUTORIAL_LESSONS: TutorialLessonData[] = [
    /* ------------------------------------------------------------------ */
    /* Lesson 1 — Row & Column Clues (3×3, letter T)                      */
    /* ------------------------------------------------------------------ */
    {
        title: 'Row & Column Clues',
        size: 3,
        solution: p([
            '111',
            '010',
            '010',
        ]),
        steps: [
            {
                heading: 'Each clue tells you how many **consecutive** filled cells are in that line.',
                text: 'Look at the **row clues** on the left — row 1 has clue **3**. Fill all 3 cells!',
                action: {
                    type: 'tap',
                    actions: [
                        {row: 0, col: 0, type: 'fill'},
                        {row: 0, col: 1, type: 'fill'},
                        {row: 0, col: 2, type: 'fill'},
                    ],
                },
                highlightCells: [
                    {row: 0, col: 0}, {row: 0, col: 1}, {row: 0, col: 2},
                ],
                errorOverrides: [
                    {row: 1, col: 1, message: 'Not yet — let\'s focus on row 1 first!', color: '#f0b830'},
                    {row: 2, col: 1, message: 'Not yet — let\'s focus on row 1 first!', color: '#f0b830'},
                ],
            },
            {
                heading: 'Each clue tells you how many **consecutive** filled cells are in that line.',
                text: 'Now look at the **column clues** on top — column 2 has clue **3**. Fill the remaining 2 cells!',
                action: {
                    type: 'tap',
                    actions: [
                        {row: 1, col: 1, type: 'fill'},
                        {row: 2, col: 1, type: 'fill'},
                    ],
                },
            },
            {
                heading: 'Each clue tells you how many **consecutive** filled cells are in that line.',
                text: 'Row clues are on the **left**, column clues are on **top**. Together they reveal the hidden picture!',
                action: {type: 'next'},
            }
        ],
    },

    /* ------------------------------------------------------------------ */
    /* Lesson 2 — Using Both Clues (3×3)                                  */
    /* ------------------------------------------------------------------ */
    {
        title: 'Using Both Clues',
        size: 3,
        solution: p([
            '000',
            '111',
            '010',
        ]),
        steps: [
            {
                heading: 'Use **row** and **column** clues together.',
                text: 'Start with row 2 — it has clue **3**. How many filled cells are there?',
                action: {
                    type: 'tap',
                    actions: [
                        {row: 1, col: 0, type: 'fill'},
                        {row: 1, col: 1, type: 'fill'},
                        {row: 1, col: 2, type: 'fill'},
                    ],
                },
                highlightCells: [
                    {row: 1, col: 0}
                ],
                errorOverrides: [
                    {row: 2, col: 1, message: 'Not yet — let\'s focus on row 2 first!', color: '#f0b830'},
                ],
            },
            {
                heading: 'Use **row** and **column** clues together.',
                text: 'Column 2 has clue **2** and one cell is already filled. Row 3 has clue **1** — where does it go?',
                action: {
                    type: 'tap',
                    actions: [
                        {row: 2, col: 1, type: 'fill'},
                    ],
                },
            },
            {
                heading: 'Use **row** and **column** clues together.',
                text: 'By combining row and column clues, you can figure out exactly where each cell goes.',
                action: {type: 'next'},
            },
        ],
    },

    /* ------------------------------------------------------------------ */
    /* Lesson 3 — Multiple Clues (3×3)                                    */
    /* ------------------------------------------------------------------ */
    {
        title: 'Multiple Clues',
        size: 3,
        solution: p([
            '101',
            '010',
            '101',
        ]),
        steps: [
            {
                heading: 'Each number is a group of filled cells, separated by **at least one** empty cell.',
                text: 'Row 1 has clue **1 1** — fill one cell on each end!',
                action: {
                    type: 'tap',
                    actions: [
                        {row: 0, col: 0, type: 'fill'},
                        {row: 0, col: 2, type: 'fill'},
                    ],
                },
                highlightCells: [
                    {row: 0, col: 0}, {row: 0, col: 2},
                ],
                errorOverrides: [
                    {row: 1, col: 1, message: 'Not yet — let\'s focus on row 1 first!', color: '#f0b830'},
                    {row: 2, col: 0, message: 'Not yet — let\'s focus on row 1 first!', color: '#f0b830'},
                    {row: 2, col: 2, message: 'Not yet — let\'s focus on row 1 first!', color: '#f0b830'},
                ],
            },
            {
                heading: 'Each number is a group of filled cells, separated by **at least one** empty cell.',
                text: 'Use the row and column clues to fill the remaining cells.',
                action: {
                    type: 'tap',
                    actions: [
                        {row: 1, col: 1, type: 'fill'},
                        {row: 2, col: 0, type: 'fill'},
                        {row: 2, col: 2, type: 'fill'},
                    ],
                },
            },
            {
                heading: 'Each number is a group of filled cells, separated by **at least one** empty cell.',
                text: 'Well done! You solved a puzzle with multiple clue groups — remember, each group needs at least one gap.',
                action: {type: 'next'},
            },
        ],
    },

    /* ------------------------------------------------------------------ */
    /* Lesson 4 — Marks & Solving (4×4)                                   */
    /* Has an empty row. Teaches mark mode, then fill.                    */
    /* ------------------------------------------------------------------ */
    {
        title: 'Marks & Solving',
        size: 4,
        solution: p([
            '0110',
            '0110',
            '0000',
            '0110',
        ]),
        showModeSwitcher: true,
        steps: [
            {
                heading: 'You can **mark** cells as empty with ✕. This helps you eliminate impossible cells and narrow down the answer.',
                text: 'Row 3 and columns 1 & 4 all have clue **0**. Use the **✕ mode** and mark them all!',
                action: {
                    type: 'tap',
                    actions: [
                        // Row 3 (all empty)
                        {row: 2, col: 0, type: 'mark'},
                        {row: 2, col: 1, type: 'mark'},
                        {row: 2, col: 2, type: 'mark'},
                        {row: 2, col: 3, type: 'mark'},
                        // Column 1 (empty except row 3 already marked)
                        {row: 0, col: 0, type: 'mark'},
                        {row: 1, col: 0, type: 'mark'},
                        {row: 3, col: 0, type: 'mark'},
                        // Column 4
                        {row: 0, col: 3, type: 'mark'},
                        {row: 1, col: 3, type: 'mark'},
                        {row: 3, col: 3, type: 'mark'},
                    ],
                },
                highlightCells: [
                    {row: 2, col: 0}, {row: 2, col: 1}, {row: 2, col: 2}, {row: 2, col: 3},
                    {row: 0, col: 0}, {row: 1, col: 0}, {row: 3, col: 0},
                    {row: 0, col: 3}, {row: 1, col: 3}, {row: 3, col: 3},
                ],
                errorOverrides: [
                    {row: 0, col: 1, message: 'Not yet — let\'s mark the empty cells first!', color: '#f0b830'},
                    {row: 0, col: 2, message: 'Not yet — let\'s mark the empty cells first!', color: '#f0b830'},
                    {row: 1, col: 1, message: 'Not yet — let\'s mark the empty cells first!', color: '#f0b830'},
                    {row: 1, col: 2, message: 'Not yet — let\'s mark the empty cells first!', color: '#f0b830'},
                    {row: 3, col: 1, message: 'Not yet — let\'s mark the empty cells first!', color: '#f0b830'},
                    {row: 3, col: 2, message: 'Not yet — let\'s mark the empty cells first!', color: '#f0b830'},
                ],
            },
            {
                heading: 'You can **mark** cells as empty with ✕. This helps you eliminate impossible cells and narrow down the answer.',
                text: 'Now switch to **fill mode** and fill the remaining cells!',
                action: {
                    type: 'tap',
                    actions: [
                        {row: 0, col: 1, type: 'fill'}, {row: 0, col: 2, type: 'fill'},
                        {row: 1, col: 1, type: 'fill'}, {row: 1, col: 2, type: 'fill'},
                        {row: 3, col: 1, type: 'fill'}, {row: 3, col: 2, type: 'fill'},
                    ],
                },
            },
            {
                heading: 'You can **mark** cells as empty with ✕. This helps you eliminate impossible cells and narrow down the answer.',
                text: 'Marks help you think through the puzzle step by step — mark what\'s empty, then fill what\'s left!',
                action: {type: 'next'},
            },
        ],
    },
];
